import classNames from "classnames/dedupe";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { FaChevronDown, FaTimes } from "react-icons/fa";
import { useOnClickOutside } from "../../helpers";

/**
 * @template {{ label?: string }} TData
 * @param {Object} props
 * @param {TData[]} [props.options]
 * @param {(opt: TData) => string} [props.getOptionLabel]
 * @param {(opt: TData) => any} [props.onClick]
 * @param {(opt: TData) => any} [props.onChange]
 * @param {string} [props.className]
 * @param {{backgroundColour: string, textColour: string, disabledBackgroundColour: string, disabledTextColour: string}} [props.theme]
 * @param {string} [props.height]
 * @param {string} [props.width]
 * @param {boolean} [props.confirm]
 * @param {string} [props.confirmationText]
 * @param {(opt: TData) => any} [props.onConfirm]
 * @param {boolean} [props.disabled]
 * @returns {JSX.Element}
 */
export const SplitButtonDropdown = ({
  options,
  getOptionLabel = opt => opt.label,
  onClick,
  onChange,
  className,
  theme,
  height,
  width,
  confirm,
  confirmationText = "Click to confirm",
  onConfirm,
  disabled
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedOption, setSelectedOption] = useState(options[0]);
  const [showConfirmationState, setShowConfirmationState] = useState(false);
  const dropdownRef = useRef();
  useOnClickOutside(dropdownRef, () => {
    setShowDropdown(false);
  });
  useEffect(() => {
    if (!options.includes(selectedOption)) setSelectedOption(options[0]);
  }, [options]);
  const classes = classNames(className, {
    "bg-en-gray-400": disabled && !showConfirmationState && !theme?.disabledBackgroundColour,
    "bg-en-gray-800": !disabled && !showConfirmationState && !theme?.backgroundColour,
    "bg-en-yellow-600 text-black": showConfirmationState && !disabled,
    "text-white": !showConfirmationState && (!theme?.textColour && !disabled || disabled && !theme?.disabledTextColour)
  }, !disabled && theme?.backgroundColour || "", !disabled && theme?.textColour || "", disabled && theme?.disabledBackgroundColour || "", disabled && theme?.disabledTextColour || "", "rounded text-center flex items-center");
  const onDropdownClicked = () => {
    !disabled && setShowDropdown(!showDropdown);
  };
  const onOptionChanged = option => {
    if (typeof onChange === "function") onChange(option);
    setSelectedOption(option);
    setShowDropdown(false);
  };
  const onButtonClicked = () => {
    !disabled && onClick && onClick(selectedOption);
    confirm && setShowConfirmationState(true);
  };
  const onConfirmConfirmed = () => {
    if (typeof onConfirm === "function") onConfirm(selectedOption);
    setShowConfirmationState(false);
  };
  const onConfirmCanceled = () => {
    setShowConfirmationState(false);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "relative width-fit-content"
  }, /*#__PURE__*/React.createElement("button", {
    className: classes,
    style: {
      ...(width ? {
        width
      } : {}),
      ...(height ? {
        height
      } : {})
    },
    disabled: disabled
  }, showConfirmationState ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "pl-3 pr-2 py-2 truncate",
    style: {
      ...(width ? {
        width
      } : {})
    },
    onClick: onConfirmConfirmed
  }, confirmationText), /*#__PURE__*/React.createElement("div", {
    className: "px-2 py-3 h-full bg-en-gray-800 text-white rounded-r-sm",
    onClick: onConfirmCanceled
  }, /*#__PURE__*/React.createElement(FaTimes, null))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: classNames("px-3 py-2 border-white truncate", {
      "border-r-2": options.length > 1
    }),
    style: {
      ...(width ? {
        width
      } : {})
    },
    onClick: onButtonClicked
  }, getOptionLabel(selectedOption)), options.length > 1 && /*#__PURE__*/React.createElement("div", {
    className: "px-1 py-2",
    onClick: onDropdownClicked
  }, /*#__PURE__*/React.createElement(FaChevronDown, null)))), showDropdown && /*#__PURE__*/React.createElement("div", {
    className: "bg-white border border-black rounded text-black z-10 width-fit-content absolute right-0",
    ref: dropdownRef
  }, options.map((option, index) => /*#__PURE__*/React.createElement("div", {
    key: `option-${index}`,
    className: `px-2 py-1 text-center cursor-pointer ${index === options.length - 1 ? "" : "border-b"}`,
    onClick: () => {
      onOptionChanged(option);
    }
  }, getOptionLabel(option)))));
};
SplitButtonDropdown.propTypes = {
  options: PropTypes.array.isRequired,
  getOptionLabel: PropTypes.func,
  onClick: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  className: PropTypes.string,
  theme: PropTypes.shape({
    backgroundColour: PropTypes.string,
    textColour: PropTypes.string,
    disabledBackgroundColour: PropTypes.string,
    disabledTextColour: PropTypes.string
  }),
  height: PropTypes.string,
  width: PropTypes.string,
  confirm: PropTypes.bool,
  confirmationText: PropTypes.string,
  onConfirm: PropTypes.func,
  disabled: PropTypes.bool
};